import { FC, SyntheticEvent, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import useForm, { validation } from '@calm-web/use-form';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';

import { ErrorBanner, SignUpForm, StyledButton, StyledLink, StyledText } from '../styles';
import { InfoFormFields } from '../types';
import {
	calculatingSelectedContractedLives,
	FORM_LOG_STRING,
	performTeamsFormValidation,
	ValidActions,
} from '../utils';
import messages from './messages';
import { Input } from './styles';
import { InfoProps, TeamsInfoFormProps } from './types';

const Info: FC<InfoProps> = ({ navigateTo, defaultValues, coveredLives }) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();

	const [error, setError] = useState<string | null>(null);
	const [showValidationEmail, setShowValidationEmail] = useState<boolean | undefined>();
	const [showValidationFirstName, setShowValidationFirstName] = useState<boolean | undefined>();
	const [showValidationLastName, setShowValidationLastName] = useState<boolean | undefined>();

	const formProps: TeamsInfoFormProps = useForm('infoForm', {
		initialModel: {
			email: defaultValues?.email ?? '',
			firstName: defaultValues?.firstName ?? '',
			lastName: defaultValues?.lastName ?? '',
		},
		validation: {
			email: validation.validateOrFail([
				{
					rules: [validation.email],
					errorResult: formatMessage(messages.infoFormEmailError),
				},
			]),
			firstName: validation.validateOrFail([
				{
					rules: [validation.required, validation.minLength(2)],
					errorResult: formatMessage(messages.infoFormFirstNameError),
				},
			]),
			lastName: validation.validateOrFail([
				{
					rules: [validation.required, validation.minLength(2)],
					errorResult: formatMessage(messages.infoFormLastNameError),
				},
			]),
		},
	});

	const _performValidation = (action: ValidActions): boolean =>
		performTeamsFormValidation({
			action,
			formProps,
			setError,
			logEvent,
			location: 'info',
			coveredLives,
		});

	const onSubmit = (e: SyntheticEvent): void => {
		e.preventDefault();

		const isValid = _performValidation('submit');
		if (!isValid) {
			return;
		}

		logEvent(FORM_LOG_STRING, {
			location: 'info',
			action: 'submit',
			status: 'success',
			variant: '7-16-24',
			selectedCoveredLives: coveredLives ?? '',
			selectedContractedLives: calculatingSelectedContractedLives(coveredLives ?? 0),
		});

		navigateTo('company', { ...formProps.model } as InfoFormFields);
	};

	const onPress = (): void => {
		if (!formProps.dirtyState.email?.hasChanged || !formProps.validation.fields.email?.isValid) {
			setShowValidationEmail(true);
		}
		if (!formProps.dirtyState.firstName?.hasChanged || !formProps.validation.fields.firstName?.isValid) {
			setShowValidationFirstName(true);
		}
		if (!formProps.dirtyState.lastName?.hasChanged || !formProps.validation.fields.lastName?.isValid) {
			setShowValidationLastName(true);
		}
	};

	return (
		<>
			{error && (
				<ErrorBanner>
					{formatMessage(messages.infoFormErrorBanner, {
						errorCount: error === 'one' ? `is ${error} item` : `are ${error} items`,
					})}
				</ErrorBanner>
			)}
			<SignUpForm onSubmit={onSubmit} id="info-form">
				<Input
					{...formProps.bindWithErrorProps('email', 'text', showValidationEmail)}
					onFocus={() => {
						setError(null);
					}}
					onBlur={() => {
						_performValidation('blur');
						setShowValidationEmail(true);
					}}
					label={formatMessage(messages.infoFormEmailLabel)}
					data-testid="get-setup-email"
				/>
				<Input
					{...formProps.bindWithErrorProps('firstName', 'text', showValidationFirstName)}
					onFocus={() => {
						setError(null);
					}}
					onBlur={() => {
						_performValidation('blur');
						setShowValidationFirstName(true);
					}}
					label={formatMessage(messages.infoFormFirstNameLabel)}
					data-testid="get-setup-first-name"
				/>
				<Input
					{...formProps.bindWithErrorProps('lastName', 'text', showValidationLastName)}
					onFocus={() => {
						setError(null);
					}}
					onBlur={() => {
						_performValidation('blur');
						setShowValidationLastName(true);
					}}
					label={formatMessage(messages.infoFormLastNameLabel)}
					data-testid="get-setup-last-name"
				/>
				<StyledButton
					type="submit"
					backgroundColor="gradientCalmBrand"
					data-testid="get-setup-next-button"
					onPress={onPress}
				>
					{formatMessage(messages.infoFormCtaCopy)}
				</StyledButton>
				<StyledText $isPrivacyAndTermsPolicy={true}>
					{formatMessage(messages.infoFormTermsAndPrivacyPolicy, {
						terms: (
							<StyledLink href="https://www.calm.com/terms" target="_blank">
								{formatMessage(messages.terms)}
							</StyledLink>
						),
						privacypolicylink: (...chunks): JSX.Element => (
							<StyledLink href="https://www.calm.com/privacy" target="_blank">
								{chunks}
							</StyledLink>
						),
					})}
				</StyledText>
			</SignUpForm>
		</>
	);
};

export default Info;
